//@ts-check
import classNames from 'classnames';
import { navigate } from 'gatsby';
import React from 'react';
import Link from '~/components/Common/Link';
import { ConvertNavigationToLinkItem } from '~/components/Hocs/useGlobalLayout';

import './DesktopHeader.scss';

/**
 * The utility header at the very top
 * @typedef DesktopMinorHeaderProps
 * @property {import('~/components/Hocs/useGlobalLayout').HeaderUtilLinks[]} links
 */
/** @type {import('~/types/React').ReactFC<DesktopMinorHeaderProps>} */
const DesktopMinorHeader = ({ className, links, ...rest }) => (
	<div {...rest} className={classNames('desktop-minor-header container', className)}>
		{/* Left FDIC logo */}
		<div className="fdic-digital-sign-post">
			<img src="/assets/images/FDIC-header-sign.svg" alt="FDIC Digital Sign" className="digital-sign" />
		</div>

		{/* Right utility links */}
		<ul className="utility-links">
			{!links?.length ? (
				<li className="empty-link-spacer" />
			) : (
				<React.Fragment>
					{links.map((link) => (
						<li
							key={`link-${link.link.href}`}
							className={classNames('utility-link-item', { 'is-icon': !!link.icon })}
							onClick={() => navigate(link.link.href)}
						>
							<Link className="utility-link" to={link.link.href}>
								{!link.icon ? (
									link.link.title
								) : (
									<img
										className="utility-icon"
										src={link.icon?.url}
										alt={link.link.title}
										aria-label={link.link.title}
									/>
								)}
							</Link>
						</li>
					))}
				</React.Fragment>
			)}
		</ul>
	</div>
);

/**
 * The primary header, with logo and Depth navigation, as well as login/open buttons.
 * @typedef DesktopMainHeaderProps
 * @property {number} [primaryIndexSelected=-1] The first page index selected.
 * @property {import('~/components/Hocs/useGlobalLayout').HeaderOptions} options
 */
/** @type {import('~/types/React').ReactFC<DesktopMainHeaderProps>} */
const DesktopMainHeader = ({ className, options, primaryIndexSelected = -1, ...rest }) => {
	return (
		<div {...rest} className={classNames('desktop-main-header', className)}>
			<div className="container">
				<div className="col-sm-3 logo-container">
					<Link to={options?.logo?.link?.href ?? '/'} aria-label={options?.logo?.link?.title}>
						<div
							className="bank-logo"
							role="button"
							title={options?.logo?.link?.title}
							aria-label={options?.logo?.link?.title}
							style={{
								backgroundImage: `url(${options?.logo?.image?.url})`,
							}}
						/>
					</Link>
				</div>

				<div className="col-sm-9 right-container">
					<ul className="primary-links">
						{ConvertNavigationToLinkItem(options?.navigation?.first_level)
							.filter((it) => !it.mobileOnly)
							.map((item, i) => (
								<li
									className={classNames('primary-link-item', { 'is-active': i === primaryIndexSelected })}
									key={`primary-link-item-${item.name}-${item.link ?? 'nolink'}`}
								>
									<Link className="primary-link" to={item.link}>
										{item.name}
									</Link>
								</li>
							))}
					</ul>

					{!!options?.open_account_button && (
						<div className="open-account">
							<Link className="open-account-link" to={options.open_account_button.href}>
								{options.open_account_button.title}
							</Link>
						</div>
					)}
					{!!options?.login_button && (
						<div className="header-login-container">
							<Link className="header-login-link btn-ozk-hover" to={options.login_button.href}>
								{options.login_button.title}
							</Link>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

/**
 * The hover menu that appears within the Sub-Nav.
 * @typedef DesktopSubHoverMenuProps
 * @property {import('../SiteLinks').LinkItem[]} childItems The items to render in the hover menu.
 */
/** @type {import('~/types/React').ReactFC<DesktopSubHoverMenuProps>} */
const DesktopSubHoverMenu = ({ className, childItems, ...rest }) => (
	<ul {...rest} className={classNames('desktop-sub-menu-list', className)}>
		{childItems.map((it, i) => (
			<li className="desktop-sub-menu-item" key={`sub-menu-item-${it.name}-${it.link ?? 'nolink'}`}>
				<Link to={it.link}>{it.name}</Link>
			</li>
		))}
	</ul>
);

/**
 * @typedef DesktopSubHeaderProps
 * @property {import('../SiteLinks').LinkItem[]} subItems The current items visible in the subMenu. Omitted if empty.
 * @property {number} [subIndexSelected=-1] The index of the item in the sub menu that is selected.
 */
/** @type {import('~/types/React').ReactFC<DesktopSubHeaderProps>} */
const DesktopSubHeader = ({ className, subItems, subIndexSelected = -1, ...rest }) =>
	subItems?.length === 0 ? null : (
		<div {...rest} className={classNames('desktop-sub-header', className)}>
			<ul className="sub-menu-list container">
				{subItems.map((si, i) => (
					<li
						className={classNames('sub-menu-item', { 'is-active': i === subIndexSelected })}
						key={`sub-menu-item-${si.name}-${si.link ?? 'nolink'}`}
					>
						<Link className="sub-menu-link" to={si.link}>
							{si.name}
						</Link>

						{/* The optional hover menu */}
						{si.children?.length > 0 && <DesktopSubHoverMenu childItems={si.children} />}
					</li>
				))}
			</ul>
		</div>
	);

/**
 * The entire desktop header.
 * @typedef DesktopHeaderProps
 * @property {string} [currentPath] The current location of the header.
 * @property {import('~/components/Hocs/useGlobalLayout').HeaderOptions} options
 */
/** @type {import('~/types/React').ReactFC<DesktopHeaderProps>} */
const DesktopHeader = ({ className, currentPath, options, ...rest }) => {
	const linkItems = ConvertNavigationToLinkItem(options?.navigation?.first_level);
	const whichIndexOpen = linkItems.findIndex((mi) => currentPath?.includes(mi.link));
	const subItems = linkItems[whichIndexOpen]?.children ?? [];
	const subIndexSelected = subItems.findIndex((si) => currentPath?.includes(si.link));

	return (
		<div {...rest} className={classNames('desktop-header', className)}>
			<DesktopMinorHeader links={options?.util_links} />
			<DesktopMainHeader primaryIndexSelected={whichIndexOpen} options={options} />
			<DesktopSubHeader subItems={subItems} subIndexSelected={subIndexSelected} />
		</div>
	);
};

export default DesktopHeader;
